import styled from 'styled-components';
import { Icon, IconName, IconSize, PersonIcon } from '@ardoq/icons';
import { text2, text2Bold, text1 } from '@ardoq/typography';
import { popoverUnderline } from '@ardoq/popovers';
import { colors, s16, s24, s32, s4, s8 } from '@ardoq/design-tokens';
import { ViewMigrationGuideLink } from 'admin/migrationChecklist/atoms';
import { Link } from '@ardoq/link';
import { orgAdminsListCommands } from 'admin/orgAdminsList/commands';
import { capitalize } from 'lodash';
import { ViewIds } from '@ardoq/api-types';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

const CantMigrateSlidesPopoverContainer = styled.div`
  ${text2}
  width: 224px;
`;

type CantMigrateSlidesPopoverProps = WithI18nLocale;
export const CantMigrateSlidesPopover = ({
  i18nLocale,
}: CantMigrateSlidesPopoverProps) => (
  <CantMigrateSlidesPopoverContainer>
    {t(
      i18nLocale,
      'viewDeprecation:cantMigrate.popover',
      "You don't have access to all of the workspaces or you don't have Write access to this presentation."
    )}
  </CantMigrateSlidesPopoverContainer>
);

export const CreatorNameAndEmailContainer = styled.div`
  margin-left: ${s4};
  margin-right: ${s4};
  flex: 1;
`;

export const SlideCreatedByEmail = styled.div`
  margin-left: ${s4};
  white-space: nowrap;
  display: inline-block;
`;

export const SlideCreatedByName = styled.div`
  ${text2Bold}
  margin-left: ${s4};
  white-space: nowrap;
  display: inline-block;
`;
export const SlideCreatedByPersonIcon = styled(PersonIcon)`
  color: ${colors.grey300};
`;
export const SlideCreatedByInfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-top: ${s4};
  margin-bottom: ${s16};
  line-height: 24px;
`;
export const WhyCantIMigrateSlidesLabel = styled.span`
  ${popoverUnderline};
  width: fit-content;
  margin-top: ${s16};
`;

export const KilldateCountdownContainer = styled.div`
  ${text2Bold}
  margin-bottom: ${s8};
`;

export const CustomDialogSubtitleSection = styled.div`
  padding: ${s32};
  border-bottom: 1px solid ${colors.grey300};
  font-size: ${s16};
  ${text2};
`;
export const SavedSettingDialogcontentBody = styled.div`
  padding: ${s32};
  ${text2};
`;

export const SavedViewModifiersRowsContainer = styled.div`
  ${text1}
  width: 100%;
  line-height: 40px;
  margin-top: ${s16};
`;

export const ValueRowContainer = styled.div`
  padding: 0 ${s8};
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;
export const DiscontinuedSettingContainer = styled(ValueRowContainer)`
  display: flex;
  align-items: center;
  background-color: ${colors.yellow50};
`;
export const StyledIcon = styled(Icon)`
  color: ${colors.yellow700};
  &:hover {
    cursor: help;
  }
`;
export const DiscontinuedSettingPopoverDiv = styled.div`
  max-width: 240px;
`;

export const NotificationMessageContainer = styled.div`
  line-height: 20px;
  margin-bottom: ${s8};
`;

export const DeprecatedSlideNotificationDetailsContainer = styled.div`
  gap: 0px;
`;

export const ViewStatusTextBadgeContainer = styled.div`
  margin: ${s24} 0 ${s8} 0;
`;

type ViewMigrationGuideLinkWithPropsArgs = WithI18nLocale;
export const ViewMigrationGuideLinkWithProps = ({
  i18nLocale,
}: ViewMigrationGuideLinkWithPropsArgs) => (
  <ViewMigrationGuideLink style={{ display: 'block', marginTop: s8 }}>
    {t(
      i18nLocale,
      'viewDeprecation:atoms.viewMigrationGuideLink',
      'Learn how to migrate views to the new version'
    )}
  </ViewMigrationGuideLink>
);

export const LeftBoundFlexContainerNoTextWrap = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-start;
`;

type ContactYourAdminLinkProps = WithI18nLocale;
export const ContactYourAdminLink = ({
  i18nLocale,
}: ContactYourAdminLinkProps) => (
  <Link
    hideIcon
    type="primary"
    typography="text2"
    onClick={() => orgAdminsListCommands.openDialog()}
  >
    {t(
      i18nLocale,
      'viewDeprecation:atoms.contactYourAdmin',
      'Contact your admin'
    )}
  </Link>
);

export const ButtonLabelLinkContainer = styled.div`
  padding-left: ${s8};
  color: ${colors.white950};
  display: flex;
  align-items: center;
`;

export const StyledButtonLabelIcon = styled(Icon)`
  margin-left: ${s8};
`;

export const DoqFaceElementContainer = styled.div`
  width: 879px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const DelteDialogMesageContainer = styled.div`
  display: flex;
  align-items: center;
  ${text2}
  color: ${colors.red700};
`;

export const DeleteDialogMessageIcon = styled(Icon)`
  margin-right: ${s16};
`;

export const BodyContainer = styled.div`
  padding: ${s32};
  display: flex;
  ${text2}
`;

export const DoqFaceContainer = styled.div`
  margin-right: ${s32};
`;

export const CallToActionMessageContainer = styled.div`
  ${text2};
  margin-bottom: ${s24};
`;

export const AlertMessageContainer = styled.div`
  ${text2Bold}
  color: ${colors.red700};
  display: flex;
  align-items: center;
`;

export const StyledIconForDeprecationCountdown = styled(Icon)`
  color: ${colors.red700};
  margin-right: ${s16};
`;

type HowToReplaceSlideMessageProps = WithI18nLocale;
export const HowToReplaceSlideMessage = ({
  i18nLocale,
}: HowToReplaceSlideMessageProps) => (
  <div>
    {t(
      i18nLocale,
      'viewDeprecation:howToReplace.intro',
      'To migrate this slide:'
    )}
    <ol>
      <li>
        {t(
          i18nLocale,
          'viewDeprecation:howToReplace.step1',
          'Select slide in the sidebar'
        )}
      </li>
      <li>
        {t(
          i18nLocale,
          'viewDeprecation:howToReplace.step2',
          'Open the new view'
        )}
      </li>
      <li>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>
            {t(
              i18nLocale,
              'viewDeprecation:howToReplace.step3prefix',
              'Select replace slide'
            )}
            &nbsp;
          </span>
          <Icon
            iconName={IconName.SYNC}
            iconSize={IconSize.LARGE}
            style={{ color: colors.grey500 }}
          />
          <span>
            &nbsp;
            {t(
              i18nLocale,
              'viewDeprecation:howToReplace.step3suffix',
              'in the sidebar'
            )}
          </span>
        </div>
      </li>
    </ol>
  </div>
);

type MigrateToImprovedSuccessorMessageArgs = {
  viewId?: ViewIds;
  i18nLocale: I18nLocale;
};
export const MigrateToImprovedSuccessorMessage = ({
  viewId,
  i18nLocale,
}: MigrateToImprovedSuccessorMessageArgs) => {
  const viewName = viewId?.split(' ').map(capitalize).join(' ') ?? '';
  return (
    <div>
      {t(
        i18nLocale,
        'viewDeprecation:atoms.improvedView',
        { viewName },
        'Migrate this slide to the improved {{viewName}} View.'
      )}
    </div>
  );
};

type ViewDiscontinuedNoPermissionsMessageArgs = {
  creatorCanMigrate?: boolean;
  i18nLocale: I18nLocale;
};
export const ViewDiscontinuedNoPermissionsMessage = ({
  creatorCanMigrate,
  i18nLocale,
}: ViewDiscontinuedNoPermissionsMessageArgs) => (
  <div>
    {creatorCanMigrate === undefined || creatorCanMigrate
      ? t(
          i18nLocale,
          'viewDeprecation:noPermissions.byCreator',
          'The view used in this slide has been discontinued and the slide should be migrated by the slide creator.'
        )
      : t(
          i18nLocale,
          'viewDeprecation:noPermissions.byAdmin',
          'The view used in this slide has been discontinued and the slide should be migrated by an admin.'
        )}
  </div>
);
